if (!customElements.get('wlsn-subcollections')) {
    /**
     * WLSNSubcollections - Custom Web Component for sticky subcollections nav and active state logic
     */
    class WLSNSubcollections extends HTMLElement {
        constructor() {
            super();
            // Define class names
            this.classes = {
                nav: 'js-wlsn-subcollections-nav',
                navButton: 'js-wlsn-subcollections-nav-button',
                section: 'js-wlsn-subcollections-collection',
                sentinel: 'js-wlsn-subcollections-nav-sentinel',
                sticky: 'sticky',
                active: 'active',
            };
            // Define elements
            this.elements = {
                nav: this.querySelector(`.${this.classes.nav}`),
                navButtons: this.querySelectorAll(`.${this.classes.navButton}`),
                sections: this.querySelectorAll(`.${this.classes.section}`),
                sentinel: this.querySelector(`.${this.classes.sentinel}`),
            };
            // Bind methods
            this.handleSectionVisibility = this.handleSectionVisibility.bind(this);
            this.handleStickyNav = this.handleStickyNav.bind(this);
        }

        /**
         * Lifecycle method called when the element is added to the DOM.
         */
        connectedCallback() {
            this.observeStickyNav();
            this.observeSections();
        }

        /**
         * Lifecycle method called when the element is removed from the DOM.
         */
        disconnectedCallback() {
            if (this.stickyObserver) this.stickyObserver.disconnect();
            if (this.sectionObserver) this.sectionObserver.disconnect();
        }

        /**
         * Observe when the navigation reaches the top of the viewport and toggle sticky class.
         */
        observeStickyNav() {
            const nav = this.elements.nav;
            if (!nav) return;
            const sentinel = this.elements.sentinel;
            if (!sentinel) return;
            this.stickyObserver = new window.IntersectionObserver(this.handleStickyNav, {
                root: null,
                threshold: 0,
            });
            this.stickyObserver.observe(sentinel);
        }

        /**
         * IntersectionObserver callback for sticky nav.
         */
        handleStickyNav(entries) {
            const nav = this.elements.nav;
            if (!nav) return;
            entries.forEach(entry => {
                if (entry.boundingClientRect.top < 0 && !entry.isIntersecting) {
                    nav.classList.add(this.classes.sticky);
                } else {
                    nav.classList.remove(this.classes.sticky);
                }
            });
        }

        /**
         * Observe visibility of sections to update the active nav button based on scroll position.
         */
        observeSections() {
            const sections = this.elements.sections;
            if (!sections.length) return;
            this.sectionObserver = new window.IntersectionObserver(this.handleSectionVisibility, {
                root: null,
                threshold: 0.5,
            });
            sections.forEach(section => {
                this.sectionObserver.observe(section);
            });
        }

        /**
         * IntersectionObserver callback for section visibility.
         * Sets active nav button and centers it in the nav.
         */
        handleSectionVisibility(entries) {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const sectionId = entry.target.getAttribute('data-subcollection-section-id');
                    this.setActiveNavButton(sectionId);
                }
            });
        }

        /**
         * Sets the active nav button by data attribute and centers it in the nav.
         * @param {string} sectionId
         */
        setActiveNavButton(sectionId) {
            this.elements.navButtons.forEach(btn => {
                if (btn.getAttribute('data-subcollection-nav-id') === sectionId) {
                    btn.classList.add(this.classes.active);
                    this.centerNavButton(btn);
                } else {
                    btn.classList.remove(this.classes.active);
                }
            });
        }

        /**
         * Scrolls the nav so the active button is centered in the scrollable parent.
         * @param {HTMLElement} btn
         */
        centerNavButton(btn) {
            const nav = this.elements.nav;
            if (!nav || !btn) return;
            const navRect = nav.getBoundingClientRect();
            const btnRect = btn.getBoundingClientRect();
            const navScrollLeft = nav.scrollLeft;
            const btnCenter = btnRect.left + btnRect.width / 2;
            const navCenter = navRect.left + navRect.width / 2;
            const scrollTo = navScrollLeft + (btnCenter - navCenter);
            nav.scrollTo({ left: scrollTo, behavior: 'smooth' });
        }
    }
    customElements.define('wlsn-subcollections', WLSNSubcollections);
}
